import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, FileText, Cookie, Robot, CreditCard, Lock, List, ArrowUp } from "@phosphor-icons/react";

const LAST_UPDATED = "April 1, 2026";
const COMPANY_NAME = "ScalitOS";
const COMPANY_ENTITY = "ScalitOS SRL";
const COMPANY_COUNTRY = "Romania, EU";
const CONTACT_EMAIL = "legal@scalitos.com";
const SUPPORT_EMAIL = "support@scalitos.com";

const SECTIONS = [
  { id: "privacy", label: "Privacy Policy", icon: Shield },
  { id: "terms", label: "Terms of Service", icon: FileText },
  { id: "cookies", label: "Cookie Notice", icon: Cookie },
  { id: "ai-disclaimer", label: "AI Disclaimer", icon: Robot },
  { id: "billing", label: "Billing Policy", icon: CreditCard },
  { id: "security", label: "Security", icon: Lock },
];

const PrivacyPolicy = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-semibold text-white mb-2 font-['Instrument_Serif',serif]">Privacy Policy</h2>
      <p className="text-sm">Last updated: {LAST_UPDATED}</p>
    </div>

    <p className="text-zinc-300 leading-relaxed">
      {COMPANY_ENTITY} ("we", "us", "our") operates the {COMPANY_NAME} platform. This Privacy Policy explains how we collect, use, store, and protect your personal data when you use our service. We are committed to protecting your privacy and complying with the General Data Protection Regulation (GDPR) and applicable EU data protection laws.
    </p>

    <Section title="1. Data We Collect">
      <p>We collect the following categories of personal data:</p>
      <SubSection title="Account Data">
        <p>When you create an account, we collect your name, email address, and an encrypted version of your password. If you subscribe to a paid plan, we process billing information through our payment processor (Stripe) — we never store your full card details on our servers.</p>
      </SubSection>
      <SubSection title="Usage Data">
        <p>We automatically collect data about how you interact with {COMPANY_NAME}, including pages visited, features used, content generated, timestamps of activity, and device/browser information. This helps us improve the platform and provide you with relevant analytics.</p>
      </SubSection>
      <SubSection title="Content Inputs">
        <p>When you use our features, we process the text, prompts, brand information, images, and other content you provide as input. This data is used exclusively to generate your requested outputs and improve your personalized experience within the platform.</p>
      </SubSection>
      <SubSection title="Uploaded Files">
        <p>If you upload images, videos, or documents as reference material for ad creation or content generation, these files are stored securely on our servers and may be analyzed by our AI systems to deliver accurate results. You retain full ownership of your uploaded content.</p>
      </SubSection>
    </Section>

    <Section title="2. How We Use Your Data">
      <ul className="list-disc pl-5 space-y-2 text-zinc-300">
        <li><strong className="text-white">Service delivery</strong> — to create your account, authenticate your sessions, process your content generation requests, and deliver the core {COMPANY_NAME} experience.</li>
        <li><strong className="text-white">Personalization</strong> — to tailor written for you outputs to your brand profile, voice DNA, and content preferences.</li>
        <li><strong className="text-white">Analytics and improvement</strong> — to understand platform usage patterns and improve our features, performance, and user experience.</li>
        <li><strong className="text-white">Billing</strong> — to process payments, manage subscriptions, and send transaction-related communications.</li>
        <li><strong className="text-white">Communication</strong> — to send you service updates, security alerts, and (with your consent) product announcements. You can opt out of marketing communications at any time.</li>
        <li><strong className="text-white">Legal compliance</strong> — to meet our legal obligations, respond to lawful requests, and protect our rights.</li>
      </ul>
    </Section>

    <Section title="3. AI Processing and Third-Party Providers">
      <p>
        {COMPANY_NAME} uses third-party AI services, including OpenAI's language models (GPT-5.2), image generation (GPT Image 1), video generation (Sora 2), and text-to-speech services to power its features. When you use features:
      </p>
      <ul className="list-disc pl-5 space-y-2 mt-3 text-zinc-300">
        <li>Your input prompts and content are sent to these providers for processing.</li>
        <li>These providers process data in accordance with their own privacy policies and data processing agreements we hold with them.</li>
        <li>We do not use your content to train AI models. Your inputs are processed transiently to generate outputs and are not retained by third-party providers beyond what is necessary for content delivery.</li>
        <li>written for you outputs are stored in your account for your access and are not shared with other users.</li>
      </ul>
    </Section>

    <Section title="4. Data Sharing">
      <p>We do not sell, rent, or trade your personal data to any third party. We share data only with:</p>
      <ul className="list-disc pl-5 space-y-2 mt-3 text-zinc-300">
        <li><strong className="text-white">Infrastructure providers</strong> — cloud hosting and database services that store your data securely under strict data processing agreements.</li>
        <li><strong className="text-white">Payment processor</strong> — Stripe processes payment transactions on our behalf under PCI-DSS compliance.</li>
        <li><strong className="text-white">AI service providers</strong> — OpenAI processes your content inputs for generation purposes only, as described above.</li>
        <li><strong className="text-white">Legal requirements</strong> — we may disclose data if required by law, court order, or to protect the safety and rights of our users or the public.</li>
      </ul>
    </Section>

    <Section title="5. Data Security">
      <p>We implement robust technical and organizational measures to protect your data:</p>
      <ul className="list-disc pl-5 space-y-2 mt-3 text-zinc-300">
        <li>All data in transit is encrypted using TLS 1.2+ (HTTPS).</li>
        <li>Passwords are hashed using bcrypt with unique salts — we never store plain-text passwords.</li>
        <li>Authentication uses time-limited JWT tokens with secure session management.</li>
        <li>Access to production systems is restricted to authorized personnel only.</li>
        <li>We conduct regular security reviews and follow industry best practices.</li>
        <li>Login attempt rate limiting and brute-force protection are active on all accounts.</li>
      </ul>
    </Section>

    <Section title="6. Your Rights Under GDPR">
      <p>As a data subject in the EU, you have the following rights:</p>
      <ul className="list-disc pl-5 space-y-2 mt-3 text-zinc-300">
        <li><strong className="text-white">Right of access</strong> — you can request a copy of the personal data we hold about you.</li>
        <li><strong className="text-white">Right to rectification</strong> — you can update or correct inaccurate data directly in your account settings or by contacting us.</li>
        <li><strong className="text-white">Right to erasure</strong> — you can request deletion of your account and all associated data. We will process this within 30 days.</li>
        <li><strong className="text-white">Right to data portability</strong> — you can request your data in a structured, machine-readable format.</li>
        <li><strong className="text-white">Right to restrict processing</strong> — you can request that we limit how we process your data in certain circumstances.</li>
        <li><strong className="text-white">Right to object</strong> — you can object to data processing based on our legitimate interests.</li>
        <li><strong className="text-white">Right to withdraw consent</strong> — where processing is based on consent, you can withdraw it at any time.</li>
      </ul>
      <p className="mt-4">To exercise any of these rights, contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-white underline underline-offset-2 hover:text-zinc-300 transition-colors">{CONTACT_EMAIL}</a>. We will respond within 30 days. You also have the right to lodge a complaint with your local data protection authority.</p>
    </Section>

    <Section title="7. Data Retention">
      <p>We retain your personal data for as long as your account is active or as needed to provide our services. Specifically:</p>
      <ul className="list-disc pl-5 space-y-2 mt-3 text-zinc-300">
        <li><strong className="text-white">Account data</strong> — retained until you delete your account.</li>
        <li><strong className="text-white">Generated content</strong> — retained in your account until you delete it or close your account.</li>
        <li><strong className="text-white">Usage analytics</strong> — aggregated and anonymized data may be retained indefinitely for service improvement.</li>
        <li><strong className="text-white">Billing records</strong> — retained for 7 years as required by Romanian tax and accounting regulations.</li>
        <li><strong className="text-white">Login and security logs</strong> — retained for 90 days for security and fraud prevention.</li>
      </ul>
      <p className="mt-3">After account deletion, we will remove your personal data within 30 days, except where retention is required by law.</p>
    </Section>

    <Section title="8. International Data Transfers">
      <p>Your data may be processed outside the European Economic Area (EEA) by our AI service providers. In such cases, we ensure appropriate safeguards are in place, including Standard Contractual Clauses (SCCs) approved by the European Commission, to protect your data to the same standard required within the EEA.</p>
    </Section>

    <Section title="9. Contact">
      <p>For privacy-related inquiries, data requests, or complaints:</p>
      <div className="mt-3 bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-5 text-zinc-300 space-y-1">
        <p><strong className="text-white">{COMPANY_ENTITY}</strong></p>
        <p>{COMPANY_COUNTRY}</p>
        <p>Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-white underline underline-offset-2">{CONTACT_EMAIL}</a></p>
      </div>
    </Section>
  </div>
);

const TermsOfService = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-semibold text-white mb-2 font-['Instrument_Serif',serif]">Terms of Service</h2>
      <p className="text-sm">Last updated: {LAST_UPDATED}</p>
    </div>

    <p className="text-zinc-300 leading-relaxed">
      These Terms of Service ("Terms") govern your access to and use of {COMPANY_NAME}, operated by {COMPANY_ENTITY}. By creating an account or using our platform, you agree to these Terms in full. If you do not agree, please do not use {COMPANY_NAME}.
    </p>

    <Section title="1. Description of Service">
      <p>
        {COMPANY_NAME} is an digital marketing operating system designed for founders, personal brands, and small business owners. The platform provides tools for generating marketing content, email campaigns, ad strategies, SEO optimization, competitor analysis, brand analytics, video ad creation, character generation, voiceover production, and carousel design.
      </p>
      <p className="mt-3">
        All content generated by {COMPANY_NAME} is produced by  systems and is intended as a starting point for your marketing efforts. Generated outputs should be reviewed, edited, and validated by you before publication or commercial use.
      </p>
    </Section>

    <Section title="2. User Accounts and Responsibilities">
      <ul className="list-disc pl-5 space-y-2 text-zinc-300">
        <li>You must be at least 18 years old to create an account.</li>
        <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
        <li>You must provide accurate and current information during registration.</li>
        <li>You are responsible for all activity that occurs under your account.</li>
        <li>You must notify us immediately at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-white underline underline-offset-2">{SUPPORT_EMAIL}</a> if you suspect unauthorized access to your account.</li>
      </ul>
    </Section>

    <Section title="3. Acceptable Use Policy">
      <p>You agree not to use {COMPANY_NAME} to:</p>
      <ul className="list-disc pl-5 space-y-2 mt-3 text-zinc-300">
        <li>Generate content that is illegal, fraudulent, defamatory, harassing, threatening, or discriminatory.</li>
        <li>Create or distribute spam, phishing materials, or misleading advertising.</li>
        <li>Produce content that infringes on third-party intellectual property, privacy, or publicity rights.</li>
        <li>Attempt to reverse-engineer, exploit, or circumvent the platform's technical protections.</li>
        <li>Use the platform for any purpose that violates applicable local, national, or international laws.</li>
        <li>Generate content related to illegal substances, weapons, exploitation, or violence.</li>
        <li>Impersonate other individuals, brands, or organizations without authorization.</li>
      </ul>
      <p className="mt-3">We reserve the right to suspend or terminate accounts that violate this policy, without prior notice or refund.</p>
    </Section>

    <Section title="4. AI-Generated Content Disclaimer">
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-5 text-zinc-300">
        <p className="font-medium text-amber-400 mb-2">Important Notice</p>
        <p>All content produced by {COMPANY_NAME} is generated by . While we strive for quality and relevance, we make <strong className="text-white">no guarantees</strong> regarding the accuracy, completeness, legal compliance, or commercial effectiveness of any generated output. written for you content may contain errors, inconsistencies, or information that is outdated or inappropriate for your specific context. You are solely responsible for reviewing, editing, and validating all content before use.</p>
      </div>
    </Section>

    <Section title="5. Subscription and Billing">
      <ul className="list-disc pl-5 space-y-2 text-zinc-300">
        <li>{COMPANY_NAME} offers free (Starter) and paid subscription tiers (Pro and Scale).</li>
        <li>Paid subscriptions are billed in advance on a monthly or annual basis.</li>
        <li>Prices are listed in USD. Applicable taxes may be added based on your jurisdiction.</li>
        <li>Subscription renewals occur automatically unless cancelled before the end of the current billing period.</li>
        <li>Cancellations take effect at the end of the current paid period — you retain access until then.</li>
        <li>We reserve the right to modify pricing with 30 days' prior notice. Price changes do not affect your current billing period.</li>
        <li>Refund requests are handled on a case-by-case basis. Contact <a href={`mailto:${SUPPORT_EMAIL}`} className="text-white underline underline-offset-2">{SUPPORT_EMAIL}</a> within 14 days of purchase.</li>
      </ul>
    </Section>

    <Section title="6. Intellectual Property">
      <SubSection title="Your Content">
        <p>You retain all ownership rights to the content you provide as input (brand profiles, text, images, documents). written for you outputs based on your inputs are licensed to you for unrestricted personal and commercial use, subject to these Terms.</p>
      </SubSection>
      <SubSection title="Our Platform">
        <p>The {COMPANY_NAME} platform — including its design, code, AI models, branding, documentation, and all proprietary materials — is owned by {COMPANY_ENTITY} and protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works of the platform itself.</p>
      </SubSection>
      <SubSection title="Feedback">
        <p>If you provide us with suggestions, ideas, or feedback about the platform, you grant us a non-exclusive, perpetual, royalty-free license to use and incorporate such feedback into our services.</p>
      </SubSection>
    </Section>

    <Section title="7. Limitation of Liability">
      <p>To the maximum extent permitted by applicable law:</p>
      <ul className="list-disc pl-5 space-y-2 mt-3 text-zinc-300">
        <li>{COMPANY_ENTITY} shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the platform.</li>
        <li>Our total liability for any claim related to the service shall not exceed the amount you paid us in the 12 months preceding the claim.</li>
        <li>We are not liable for any losses resulting from your reliance on written for you content, including but not limited to lost revenue, reputation damage, or regulatory penalties.</li>
        <li>We do not guarantee uninterrupted, error-free service. Scheduled and unscheduled downtime may occur.</li>
      </ul>
    </Section>

    <Section title="8. Account Termination">
      <ul className="list-disc pl-5 space-y-2 text-zinc-300">
        <li>You may delete your account at any time through the Settings page or by contacting {SUPPORT_EMAIL}.</li>
        <li>We may suspend or terminate your account if you violate these Terms, engage in fraudulent activity, or fail to pay applicable fees.</li>
        <li>Upon termination, your data will be deleted within 30 days, except where retention is required by law.</li>
        <li>Termination does not entitle you to a refund for the current billing period, except as required by applicable consumer protection laws.</li>
      </ul>
    </Section>

    <Section title="9. Modifications to Terms">
      <p>We may update these Terms from time to time. If we make material changes, we will notify you via email or through the platform at least 14 days before the changes take effect. Your continued use of {COMPANY_NAME} after changes become effective constitutes acceptance of the updated Terms.</p>
    </Section>

    <Section title="10. Governing Law and Disputes">
      <p>
        These Terms are governed by and construed in accordance with the laws of Romania and applicable European Union regulations. Any disputes arising from these Terms or your use of {COMPANY_NAME} shall be submitted to the competent courts of Bucharest, Romania. Nothing in these Terms affects your rights as a consumer under mandatory provisions of the law of your country of residence.
      </p>
    </Section>
  </div>
);

const CookieNotice = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-semibold text-white mb-2 font-['Instrument_Serif',serif]">Cookie Notice</h2>
      <p className="text-sm">Last updated: {LAST_UPDATED}</p>
    </div>

    <p className="text-zinc-300 leading-relaxed">
      {COMPANY_NAME} uses cookies and similar technologies to provide, protect, and improve our platform. This notice explains what cookies we use and how you can manage them.
    </p>

    <Section title="1. What Are Cookies?">
      <p>Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences, keep you logged in, and understand how you interact with the service.</p>
    </Section>

    <Section title="2. Cookies We Use">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-zinc-300 mt-2">
          <thead>
            <tr className="border-b border-zinc-700/50">
              <th className="text-left py-3 pr-4 text-white font-medium">Category</th>
              <th className="text-left py-3 pr-4 text-white font-medium">Purpose</th>
              <th className="text-left py-3 text-white font-medium">Duration</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            <tr>
              <td className="py-3 pr-4 font-medium text-white">Essential</td>
              <td className="py-3 pr-4">Authentication (keeping you logged in), security tokens, session management. These are strictly necessary for the platform to function.</td>
              <td className="py-3">Session / 7 days</td>
            </tr>
            <tr>
              <td className="py-3 pr-4 font-medium text-white">Functional</td>
              <td className="py-3 pr-4">Remembering your preferences such as theme settings, language, and recently viewed pages.</td>
              <td className="py-3">30 days</td>
            </tr>
            <tr>
              <td className="py-3 pr-4 font-medium text-white">Analytics</td>
              <td className="py-3 pr-4">Understanding how users interact with {COMPANY_NAME} so we can improve features and performance. Data is aggregated and anonymized.</td>
              <td className="py-3">1 year</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-zinc-400 text-sm">We do not use advertising cookies or third-party tracking cookies for ad targeting.</p>
    </Section>

    <Section title="3. Your Consent">
      <p>Essential cookies are set automatically as they are required for the platform to operate. For analytics and functional cookies, we rely on your consent, which you can provide or withdraw at any time.</p>
    </Section>

    <Section title="4. Managing Cookies">
      <p>You can control cookies through:</p>
      <ul className="list-disc pl-5 space-y-2 mt-3 text-zinc-300">
        <li><strong className="text-white">Browser settings</strong> — most browsers allow you to block or delete cookies through their privacy settings. Note that blocking essential cookies may prevent {COMPANY_NAME} from functioning properly.</li>
        <li><strong className="text-white">Account settings</strong> — you can manage your data and privacy preferences within {COMPANY_NAME}.</li>
      </ul>
      <p className="mt-3">For guidance on managing cookies in specific browsers, visit your browser's help documentation.</p>
    </Section>

    <Section title="5. Local Storage">
      <p>{COMPANY_NAME} also uses browser local storage to securely store your authentication token. This is functionally similar to a cookie and is cleared when you log out or clear your browser data.</p>
    </Section>
  </div>
);

const AIDisclaimer = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-semibold text-white mb-2 font-['Instrument_Serif',serif]">AI Content Disclaimer</h2>
      <p className="text-sm">Last updated: {LAST_UPDATED}</p>
    </div>

    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Robot size={28} className="text-amber-400 flex-shrink-0" />
        <h3 className="text-lg font-medium text-amber-400">AI-Generated Content Notice</h3>
      </div>
      <p className="text-zinc-300 leading-relaxed">
        All marketing content, strategies, analytics insights, ad copy, email campaigns, SEO recommendations, video ads, character images, carousels, and voiceovers generated through {COMPANY_NAME} are produced by  systems.
      </p>
    </div>

    <Section title="1. No Guarantee of Accuracy">
      <p>written for you content may contain factual errors, outdated information, culturally inappropriate references, or claims that require verification. {COMPANY_NAME} does not guarantee the accuracy, completeness, or reliability of any generated output. Content is produced probabilistically by language models and should always be treated as a draft, not a final product.</p>
    </Section>

    <Section title="2. No Guarantee of Results">
      <p>Marketing outcomes depend on countless variables beyond the quality of your content — including timing, audience behavior, market conditions, competition, platform algorithms, and more. {COMPANY_NAME} provides tools and suggestions to support your marketing efforts, but we make no guarantees regarding engagement rates, conversion rates, revenue growth, follower growth, or any other business metric.</p>
    </Section>

    <Section title="3. No Guarantee of Compliance">
      <p>written for you content is not reviewed for legal compliance. It is your responsibility to ensure that any content you publish or distribute complies with:</p>
      <ul className="list-disc pl-5 space-y-2 mt-3 text-zinc-300">
        <li>Advertising standards and regulations in your jurisdiction</li>
        <li>Industry-specific rules (e.g., financial services, healthcare, alcohol, tobacco)</li>
        <li>Platform-specific terms of service (Instagram, Facebook, LinkedIn, TikTok, etc.)</li>
        <li>Intellectual property and trademark laws</li>
        <li>Consumer protection and truth-in-advertising requirements</li>
        <li>Data protection and privacy regulations (GDPR, CCPA, etc.)</li>
      </ul>
    </Section>

    <Section title="4. User Responsibility">
      <p className="font-medium text-white">You are solely responsible for:</p>
      <ul className="list-disc pl-5 space-y-2 mt-3 text-zinc-300">
        <li>Reviewing and editing all written for you content before publication.</li>
        <li>Verifying factual claims, statistics, and product information.</li>
        <li>Ensuring content is appropriate for your audience and context.</li>
        <li>Complying with all applicable laws and platform policies.</li>
        <li>Any consequences — legal, financial, or reputational — arising from your use of written for you content.</li>
      </ul>
    </Section>

    <Section title="5. AI Image and Video Content">
      <p>Images generated through our Character Generator (GPT Image 1) and videos generated through our Ad Creator (Sora 2) are AI-produced. These outputs may not perfectly represent real-world physics, human anatomy, or product details. Generated visuals should be reviewed for quality and accuracy before use in advertisements or public-facing materials.</p>
    </Section>

    <Section title="6. Voiceover Content">
      <p>written for you voiceovers produced through our Audio Studio use synthetic speech technology. The resulting audio is not a recording of a real person. You are responsible for ensuring that the use of synthetic voices complies with applicable regulations regarding written for you media and disclosure requirements in your jurisdiction.</p>
    </Section>
  </div>
);

const BillingPolicy = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-semibold text-white mb-2 font-['Instrument_Serif',serif]">Pricing & Billing Policy</h2>
      <p className="text-sm">Last updated: {LAST_UPDATED}</p>
    </div>

    <p className="text-zinc-300 leading-relaxed">
      {COMPANY_NAME} is committed to transparent and fair billing. This policy explains how our pricing, billing cycles, and cancellation process work.
    </p>

    <Section title="1. Subscription Plans">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-zinc-300 mt-2">
          <thead>
            <tr className="border-b border-zinc-700/50">
              <th className="text-left py-3 pr-4 text-white font-medium">Plan</th>
              <th className="text-left py-3 pr-4 text-white font-medium">Monthly</th>
              <th className="text-left py-3 pr-4 text-white font-medium">Annual</th>
              <th className="text-left py-3 text-white font-medium">Includes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            <tr>
              <td className="py-3 pr-4 font-medium text-white">Starter</td>
              <td className="py-3 pr-4">Free</td>
              <td className="py-3 pr-4">Free</td>
              <td className="py-3">Basic content generation, 5 AI generations/day</td>
            </tr>
            <tr>
              <td className="py-3 pr-4 font-medium text-white">Pro</td>
              <td className="py-3 pr-4">$29/mo</td>
              <td className="py-3 pr-4">$290/yr</td>
              <td className="py-3">Full Content & Copy Studios, Brand Analyzer, SEO, Email, 50 AI generations/day</td>
            </tr>
            <tr>
              <td className="py-3 pr-4 font-medium text-white">Scale</td>
              <td className="py-3 pr-4">$79/mo</td>
              <td className="py-3 pr-4">$790/yr</td>
              <td className="py-3">Everything in Pro + Meta Ads, Video Ads, Characters, Carousel, unlimited AI</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-zinc-400 text-sm">Prices are in USD. Annual plans offer approximately 2 months free compared to monthly billing.</p>
    </Section>

    <Section title="2. Billing Cycles">
      <ul className="list-disc pl-5 space-y-2 text-zinc-300">
        <li><strong className="text-white">Monthly plans</strong> are billed on the same date each month (e.g., if you subscribe on March 15, you'll be billed on the 15th of each following month).</li>
        <li><strong className="text-white">Annual plans</strong> are billed as a single payment at the start of each 12-month period.</li>
        <li>Payments are processed securely through Stripe. We accept major credit and debit cards.</li>
        <li>You will receive an email receipt for every payment.</li>
      </ul>
    </Section>

    <Section title="3. Cancellation Policy">
      <ul className="list-disc pl-5 space-y-2 text-zinc-300">
        <li>You can cancel your subscription at any time from your Billing page in the dashboard.</li>
        <li>Cancellation takes effect at the end of your current billing period — you continue to have full access until then.</li>
        <li>No partial refunds are issued for unused time within a billing period.</li>
        <li>After cancellation, your account reverts to the free Starter plan. Your data and generated content remain accessible.</li>
      </ul>
    </Section>

    <Section title="4. Refund Policy">
      <p>
        We handle refund requests on a case-by-case basis. If you are unsatisfied with {COMPANY_NAME}, contact us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-white underline underline-offset-2">{SUPPORT_EMAIL}</a> within <strong className="text-white">14 days of your purchase</strong> and we will work with you to find a fair resolution.
      </p>
      <p className="mt-3">
        As required by EU consumer protection law, if you are based in the EU, you have the right to withdraw from a digital service contract within 14 days of purchase, provided you have not fully used the service during that period.
      </p>
    </Section>

    <Section title="5. No Hidden Fees">
      <p>The price you see on our pricing page is the price you pay. There are no setup fees, hidden charges, or surprise costs. If applicable taxes apply based on your location, they will be clearly displayed before you complete your purchase.</p>
    </Section>

    <Section title="6. Price Changes">
      <p>If we change our prices, we will notify you at least 30 days in advance via email. Price changes will not affect your current billing period. You can choose to accept the new pricing or cancel your subscription before it takes effect.</p>
    </Section>

    <Section title="7. Failed Payments">
      <p>If a payment fails, we will attempt to process it again over the following 7 days. If payment cannot be collected, your account may be downgraded to the Starter plan. We will notify you via email before any downgrade occurs.</p>
    </Section>
  </div>
);

const SecurityStatement = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-semibold text-white mb-2 font-['Instrument_Serif',serif]">Security Statement</h2>
      <p className="text-sm">Last updated: {LAST_UPDATED}</p>
    </div>

    <p className="text-zinc-300 leading-relaxed">
      At {COMPANY_NAME}, protecting your data is a core priority. We employ industry-standard security practices to safeguard your account, content, and personal information. This statement outlines the measures we take and our commitment to maintaining a secure platform.
    </p>

    <Section title="1. Authentication & Access Control">
      <ul className="list-disc pl-5 space-y-2 text-zinc-300">
        <li><strong className="text-white">Password security</strong> — all passwords are hashed using bcrypt with unique random salts. We never store, log, or transmit plain-text passwords.</li>
        <li><strong className="text-white">Token-based sessions</strong> — authentication uses short-lived JWT access tokens (2-hour expiry) with secure refresh token rotation.</li>
        <li><strong className="text-white">Brute-force protection</strong> — automated rate limiting locks accounts after 5 failed login attempts for 15 minutes, preventing credential-stuffing attacks.</li>
        <li><strong className="text-white">Secure password reset</strong> — password reset tokens are cryptographically random, single-use, and expire after 1 hour.</li>
      </ul>
    </Section>

    <Section title="2. Data Protection">
      <ul className="list-disc pl-5 space-y-2 text-zinc-300">
        <li><strong className="text-white">Encryption in transit</strong> — all data transmitted between your browser and our servers is encrypted using TLS 1.2 or higher (HTTPS).</li>
        <li><strong className="text-white">Encryption at rest</strong> — database storage uses encrypted volumes to protect data at rest.</li>
        <li><strong className="text-white">Minimal data exposure</strong> — API responses are designed to exclude internal identifiers and sensitive fields. We follow the principle of least privilege in data access.</li>
        <li><strong className="text-white">Input validation</strong> — all user inputs are validated server-side using strict Pydantic models to prevent injection attacks and malformed data processing.</li>
      </ul>
    </Section>

    <Section title="3. Infrastructure Security">
      <ul className="list-disc pl-5 space-y-2 text-zinc-300">
        <li>Our platform runs on managed cloud infrastructure with automated security patches and updates.</li>
        <li>Network access to internal services is restricted by firewall rules and private networking.</li>
        <li>Application logs are monitored for anomalies and suspicious activity.</li>
        <li>We maintain separate development and production environments to prevent accidental data exposure.</li>
      </ul>
    </Section>

    <Section title="4. Payment Security">
      <p>All payment processing is handled by Stripe, which is PCI-DSS Level 1 certified — the highest level of payment security. {COMPANY_NAME} does not store, process, or transmit your credit card details directly. Payment data flows exclusively through Stripe's secure infrastructure.</p>
    </Section>

    <Section title="5. Third-Party AI Providers">
      <p>When your content is processed by third-party AI providers (such as OpenAI), it is transmitted over encrypted connections. We maintain data processing agreements with our AI providers that contractually limit how your data can be used. Your content inputs are not used to train third-party AI models.</p>
    </Section>

    <Section title="6. Our Commitment">
      <p>While no system can guarantee absolute security, we are committed to taking reasonable and appropriate measures to protect your data. We continuously review and improve our security practices as threats evolve. If you discover a security vulnerability, please report it responsibly to <a href={`mailto:${CONTACT_EMAIL}`} className="text-white underline underline-offset-2">{CONTACT_EMAIL}</a>.</p>
    </Section>
  </div>
);

// Reusable components
const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h3 className="text-lg font-medium text-white">{title}</h3>
    <div className="text-zinc-300 leading-relaxed space-y-3">{children}</div>
  </div>
);

const SubSection = ({ title, children }) => (
  <div className="mt-3">
    <h4 className="text-sm font-semibold text-zinc-200 uppercase tracking-wide mb-2">{title}</h4>
    <div className="text-zinc-300 leading-relaxed">{children}</div>
  </div>
);

const SECTION_COMPONENTS = {
  "privacy": PrivacyPolicy,
  "terms": TermsOfService,
  "cookies": CookieNotice,
  "ai-disclaimer": AIDisclaimer,
  "billing": BillingPolicy,
  "security": SecurityStatement,
};

export default function LegalPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(section || "privacy");

  useEffect(() => {
    if (section && SECTION_COMPONENTS[section]) {
      setActiveSection(section);
    } else if (!section) {
      setActiveSection("privacy");
    }
  }, [section]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeSection]);

  const SectionComponent = SECTION_COMPONENTS[activeSection] || PrivacyPolicy;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="legal-page">
      {/* Header */}
      <header className="border-b border-zinc-800/50 sticky top-0 bg-black/90 backdrop-blur-md z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 group" data-testid="legal-logo-link">
              <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                <span className="text-black font-bold text-sm">SO</span>
              </div>
              <span className="text-white font-semibold group-hover:text-zinc-300 transition-colors">ScalitOS</span>
            </Link>
            <span className="text-zinc-600 hidden sm:block">/</span>
            <span className="text-zinc-400 text-sm hidden sm:block">Legal</span>
          </div>
          <Link
            to="/"
            data-testid="legal-back-home"
            className="text-sm text-zinc-400 hover:text-white transition-colors flex items-center gap-1.5"
          >
            <ArrowLeft size={14} />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Sidebar navigation */}
          <nav className="lg:w-64 flex-shrink-0" data-testid="legal-nav">
            <div className="lg:sticky lg:top-24 space-y-1">
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-medium mb-3 px-3">Legal Documents</p>
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const isActive = activeSection === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => {
                      setActiveSection(s.id);
                      navigate(`/legal/${s.id}`, { replace: true });
                    }}
                    data-testid={`legal-nav-${s.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                      isActive
                        ? "bg-white/10 text-white"
                        : "text-zinc-400 hover:text-zinc-200 hover:bg-white/5"
                    }`}
                  >
                    <Icon size={18} weight={isActive ? "fill" : "regular"} />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Content */}
          <main className="flex-1 min-w-0 max-w-3xl" data-testid="legal-content">
            <SectionComponent />

            {/* Footer nav */}
            <div className="mt-16 pt-8 border-t border-zinc-800/50">
              <div className="flex flex-wrap gap-3">
                {SECTIONS.filter(s => s.id !== activeSection).map(s => (
                  <button
                    key={s.id}
                    onClick={() => {
                      setActiveSection(s.id);
                      navigate(`/legal/${s.id}`, { replace: true });
                    }}
                    className="text-sm text-zinc-500 hover:text-white transition-colors px-3 py-1.5 rounded-full border border-zinc-800 hover:border-zinc-600"
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              <div className="mt-8 bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-5">
                <p className="text-zinc-400 text-sm leading-relaxed">
                  If you have any questions about these policies or your rights, please contact us at{" "}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-white underline underline-offset-2 hover:text-zinc-300 transition-colors">{CONTACT_EMAIL}</a>.
                  We are committed to transparency and will respond to all inquiries within 30 days.
                </p>
              </div>

              <p className="mt-6 text-zinc-600 text-xs text-center">
                {COMPANY_ENTITY} &middot; {COMPANY_COUNTRY} &middot; All rights reserved &copy; {new Date().getFullYear()}
              </p>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
